import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Button, Card, CardContent, TextField, Table,
    TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
    MenuItem, Stack, Divider, Avatar, IconButton, Tooltip, Container,
    Snackbar, Alert, InputAdornment, AppBar, Toolbar
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    AddCircle, Cancel, Close, Save,
    Add as AddIcon,
    ArrowBack as ArrowBackIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    PersonAdd as PersonAddIcon,
    Email as EmailIcon,
    Phone as PhoneIcon,
    CalendarMonth as CalendarIcon,
    Badge as BadgeIcon,
    School as SchoolIcon,
    Business
} from '@mui/icons-material';

// Constants
const MAX_IMAGE_WIDTH = 800;
const MAX_IMAGE_HEIGHT = 800;
const API_BASE_URL = 'http://155.133.27.173:3020';

// Theme configuration
const theme = createTheme({
    palette: {
        primary: { main: '#1976d2' },
        secondary: { main: '#dc004e' },
        background: { default: '#f5f5f5' },
    },
    typography: {
        h4: { fontWeight: 600 },
        h6: { fontWeight: 500 },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 8,
                    textTransform: 'none',
                    fontWeight: 600,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                },
            },
        },
        MuiTableCell: {
            styleOverrides: {
                head: {
                    fontWeight: 'bold',
                    backgroundColor: '#f5f5f5',
                },
            },
        },
    },
});

// Styled components
const StyledTableCell = styled(TableCell)(() => ({
    fontWeight: 'bold',
    fontSize: '0.875rem',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(1),
    textTransform: 'none',
    fontWeight: 'bold',
}));

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

// Initial form state
const initialFormState = {
    name: '',
    phone: '',
    email: '',
    joining: '',
    device: '',
    adhaarNo: '',
    admissionNo: '',
    birth: '',
    gender: '',
    company: '',
    department: '',
    designation: '',
    shift: '',
};

const EmployeeManagement = () => {
    // State variables
    const [screen, setScreen] = useState('read');
    const [selectedItem, setSelectedItem] = useState(null);
    const [employees, setEmployees] = useState([]);
    const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });
    const [photo, setPhoto] = useState(null);
    const [formValues, setFormValues] = useState(initialFormState);
    const [activeRegister, setActiveRegister] = useState(false);

    // Options for dropdowns
    const [shiftPlans, setShiftPlans] = useState([]);
    const [companyOptions, setCompanyOptions] = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);
    const [designationOptions, setDesignationOptions] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);

    const genders = [
        { value: 'male', label: 'Male' },
        { value: 'female', label: 'Female' },
        { value: 'others', label: 'Others' }
    ];

    // Helper function to show alerts
    const showAlert = (message, severity = 'info') => {
        setAlert({ open: true, message, severity });
    };

    // Handle close alert
    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };

    // Fetch all necessary data on component mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        const userID = localStorage.getItem('userID');
        const headers = { 'Authorization': `Bearer ${token}` };

        // Helper function to fetch data from API
        const fetchData = async (endpoint, stateSetter) => {
            try {
                const response = await fetch(`${API_BASE_URL}/${endpoint}/${userID}`, { headers });
                const data = await response.json();
                if (data.length > 0) {
                    stateSetter(data);
                }
            } catch (error) {
                console.error(`Error fetching ${endpoint}:`, error);
                showAlert(`Failed to load ${endpoint}`, 'error');
            }
        };
        fetchData('get-employees', setEmployees);
        fetchData('all-shift-plan', setShiftPlans);
        fetchData('get-company', setCompanyOptions);
        fetchData('get-departments', setDepartmentOptions);
        fetchData('get-designation', setDesignationOptions);
        fetchData('get-devices', setDeviceOptions);
    }, []);

    // Handle form input changes
    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormValues((prevValues) => ({
            ...prevValues,
            [name]: value,
        }));
    };

    // Reset form
    const resetForm = () => {
        setFormValues(initialFormState);
        setPhoto(null);
    };

    // Toggle between screens
    const toggleScreen = (newScreen) => {
        if (newScreen === 'write') {
            resetForm();
        }
        setScreen(newScreen);
    };

    // Handle photo file selection and validation
    const handlePhotoChange = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            const img = new Image();
            img.src = e.target.result;

            img.onload = () => {
                if (img.width > MAX_IMAGE_WIDTH || img.height > MAX_IMAGE_HEIGHT) {
                    showAlert(`Image resolution must be less than ${MAX_IMAGE_WIDTH}x${MAX_IMAGE_HEIGHT}`, 'warning');
                    return;
                }

                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");
                canvas.width = img.width;
                canvas.height = img.height;
                ctx.drawImage(img, 0, 0, img.width, img.height);

                canvas.toBlob((blob) => {
                    setPhoto(blob);
                }, file.type);
            };
        };
        reader.readAsDataURL(file);

        // Validate face in image
        try {
            const formData = new FormData();
            formData.append('photo', file);
            const response = await fetch(`${API_BASE_URL}/check-face`, {
                method: 'POST',
                body: formData,
            });

            const result = await response.json();
            if (result.result[0]._label === 'unknown') {
                setActiveRegister(true);
                showAlert('Image is valid. Please proceed with registration!', 'success');
            } else {
                showAlert('Image is valid. Please proceed with registration!', 'info');
            }
        } catch (error) {
            console.error('Failed to validate face:', error);
            showAlert('Failed to validate image', 'error');
        }
    };

    // Refresh employee list
    const refreshEmployeeList = async () => {
        try {
            const userID = localStorage.getItem('userID');
            const response = await fetch(`${API_BASE_URL}/get-employees/${userID}`, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`
                }
            });
            const data = await response.json();
            if (data.length > 0) {
                setEmployees(data);
            }
        } catch (error) {
            console.error('Error refreshing employee list:', error);
            showAlert('Failed to refresh employee list', 'error');
        }
    };

    // Handle adding a new employee
    const handleAddEmployee = async () => {
        // Validation
        if (!photo) {
            showAlert('Please upload a photo', 'error');
            return;
        }

        if (!formValues.name || !formValues.phone || !formValues.email) {
            showAlert('Please fill in all required fields', 'error');
            return;
        }

        const formData = new FormData();
        formData.append("userID", localStorage.getItem("userID"));

        // Append all form values to formData
        console.log(formValues);

        Object.entries(formValues).forEach(([key, value]) => {
            formData.append(key, value);
        });
        console.log(formData);
        formData.append('photo', photo);

        try {
            // First API call to post employee data
            const response = await fetch(`${API_BASE_URL}/post-employees`, {
                method: 'POST',
                body: formData,
            });

            if (response.ok) {
                showAlert('Registration completed successfully!', 'success');
                toggleScreen('read');
                await refreshEmployeeList();
            } else {
                showAlert('Failed to add employee', 'error');
            }
        } catch (error) {
            console.error('Failed to add employee:', error);
            showAlert('An error occurred during registration', 'error');
        }
    };

    // Handle updating an existing employee
    const handleUpdateEmployee = async (event) => {
        event.preventDefault();

        try {
            const response = await fetch(`${API_BASE_URL}/modify-company/${selectedItem._id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                },
                body: JSON.stringify({
                    name: formValues.name,
                    email: formValues.email,
                    phone: formValues.phone,
                    location: formValues.location
                }),
            });

            const result = await response.json();
            if (result._id) {
                showAlert('Employee updated successfully', 'success');
                toggleScreen('read');
                await refreshEmployeeList();
            } else {
                showAlert('Error updating employee', 'error');
            }
        } catch (error) {
            console.error('Error updating employee:', error);
            showAlert('Update failed', 'error');
        }
    };

    // Handle deleting an employee
    const handleDeleteEmployee = async (id) => {
        if (window.confirm('Are you sure you want to delete this record?')) {
            try {
                const response = await fetch(`${API_BASE_URL}/delete-company/${id}`, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('token')}`
                    }
                });

                if (response.ok) {
                    setEmployees(employees.filter(emp => emp._id !== id));
                    showAlert('Employee deleted successfully', 'success');
                } else {
                    showAlert('Failed to delete employee', 'error');
                }
            } catch (error) {
                console.error('Error deleting employee:', error);
                showAlert('Delete operation failed', 'error');
            }
        }
    };

    // Handle edit button click
    const handleEdit = (item) => {
        setSelectedItem(item);
        setFormValues({
            name: item.name || '',
            email: item.email || '',
            phone: item.phone || '',
            location: item.location || '',
            adhaarNo: item.adhaarNo || '',
            admissionNo: item.admissionNo || '',
            birth: item.birth || '',
            gender: item.gender || '',
            device: item.device?._id || '',
            joining: item.joining || '',
        });
        toggleScreen('modify');
    };

    // Common form fields component
    const FormFields = ({ isEdit = false }) => (
        <Stack spacing={3}>
            {/* Name and Phone */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Name"
                    name="name"
                    value={formValues.name}
                    onChange={handleFormChange}
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <BadgeIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Phone"
                    name="phone"
                    value={formValues.phone}
                    onChange={handleFormChange}
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <PhoneIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {/* Email and Registration No */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Email"
                    name="email"
                    value={formValues.email}
                    onChange={handleFormChange}
                    required
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <EmailIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    fullWidth
                    variant="outlined"
                    label="Registration No."
                    name="admissionNo"
                    value={formValues.admissionNo}
                    onChange={handleFormChange}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SchoolIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {/* Only show company/department fields in create form */}
            {!isEdit && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        select
                        fullWidth
                        label="Company"
                        name="company"
                        variant="outlined"
                        value={formValues.company}
                        onChange={handleFormChange}
                        required>
                        {companyOptions.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        label="Department"
                        name="department"
                        variant="outlined"
                        value={formValues.department}
                        onChange={handleFormChange}
                        required
                    >
                        {departmentOptions.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            )}

            {/* Only show designation in create form */}
            {!isEdit && (
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField
                        select
                        fullWidth
                        label="Designation"
                        name="designation"
                        variant="outlined"
                        value={formValues.designation}
                        onChange={handleFormChange}
                        required
                    >
                        {designationOptions.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                                {option.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    <TextField
                        select
                        fullWidth
                        label="Device"
                        name="device"
                        variant="outlined"
                        value={formValues.device}
                        onChange={handleFormChange}
                        required
                    >
                        {deviceOptions.map((option) => (
                            <MenuItem key={option._id} value={option._id}>
                                {option.deviceName}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            )}

            {/* Device (only field if edit mode) */}
            {isEdit && (
                <TextField
                    select
                    fullWidth
                    label="Device"
                    name="device"
                    variant="outlined"
                    value={formValues.device}
                    onChange={handleFormChange}
                    required
                >
                    {deviceOptions.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                            {option.deviceName}
                        </MenuItem>
                    ))}
                </TextField>
            )}

            {/* Adhaar No and Date of Joining */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    select
                    fullWidth
                    label="Shift Plan"
                    name="shift"
                    variant="outlined"
                    value={formValues.shift}
                    onChange={handleFormChange}
                    required
                >
                    {shiftPlans.map((option) => (
                        <MenuItem key={option._id} value={option._id}>
                            {option.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    label="Date of Joining"
                    name="joining"
                    value={formValues.joining}
                    onChange={handleFormChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CalendarIcon />
                            </InputAdornment>
                        ),
                    }}
                />
            </Stack>

            {/* Birth Date and Gender */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                <TextField
                    fullWidth
                    variant="outlined"
                    type="date"
                    label="Birth Date"
                    name="birth"
                    value={formValues.birth}
                    onChange={handleFormChange}
                    InputLabelProps={{
                        shrink: true,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <CalendarIcon />
                            </InputAdornment>
                        ),
                    }}
                />
                <TextField
                    select
                    fullWidth
                    label="Gender"
                    name="gender"
                    variant="outlined"
                    value={formValues.gender}
                    onChange={handleFormChange}
                    required
                >
                    {genders.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </TextField>
            </Stack>
        </Stack>
    );

    // Render the employee table
    const renderEmployeeTable = () => (
        <TableContainer component={Paper} elevation={2} sx={{ mt: 3, borderRadius: 2, overflow: 'hidden' }}>
            <Table aria-label="employee table">
                <TableHead>
                    <TableRow>
                        <TableCell>Photo</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Company</TableCell>
                        <TableCell>Department</TableCell>
                        <TableCell>Designation</TableCell>
                        <TableCell>Email</TableCell>
                        <TableCell>Phone</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Actions</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {employees.map((employee) => (
                        <TableRow key={employee._id} hover>
                            <TableCell>
                                <Avatar
                                    src={`${API_BASE_URL}/${employee.photoUrl}`}
                                    alt={employee.name}
                                    sx={{ width: 40, height: 40 }}
                                />
                            </TableCell>
                            <TableCell>{employee.name}</TableCell>
                            <TableCell>
                                <Box display="flex" alignItems="center">
                                    <Business sx={{ mr: 1, color: "primary.main" }} />
                                    {employee.company?.name || "N/A"}
                                </Box>
                            </TableCell>
                            <TableCell>{employee.department?.name || 'N/A'}</TableCell>
                            <TableCell>{employee.designation?.name || 'N/A'}</TableCell>
                            <TableCell>{employee.email}</TableCell>
                            <TableCell>{employee.phone}</TableCell>
                            <TableCell style={{ fontWeight: 'bold', color: employee.hasNumbers ? 'green' : 'red' }}>{employee.hasNumbers ? 'Active' : 'InActive'}</TableCell>
                            <TableCell>
                                <Stack direction="row" spacing={1} justifyContent="center">
                                    <Tooltip title="Edit">
                                        <IconButton
                                            color="primary"
                                            size="small"
                                            onClick={() => handleEdit(employee)}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            color="error"
                                            size="small"
                                            onClick={() => handleDeleteEmployee(employee._id)}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    // Render the create employee form
    const renderCreateForm = () => (
        <Card
            sx={{
                mt: 3,
                mb: 3,
                pb: 3,
                pt: 3
            }}
            elevation={3}
        >
            <CardContent>
                <FormFields />

                {/* Photo Upload */}
                <Box mt={3}>
                    <Button
                        component="label"
                        variant="contained"
                        startIcon={<AddIcon />}
                        sx={{ mb: 2 }}
                    >
                        Upload Photo
                        <VisuallyHiddenInput
                            type="file"
                            accept="image/*"
                            onChange={handlePhotoChange}
                        />
                    </Button>

                    {photo && (
                        <Box mt={2} display="flex" justifyContent="center">
                            <Avatar
                                src={URL.createObjectURL(photo)}
                                alt="Preview"
                                sx={{ width: 120, height: 120 }}
                                variant="rounded"
                            />
                        </Box>
                    )}
                </Box>

                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => toggleScreen('read')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        disabled={!photo}
                        startIcon={<Save />}
                        onClick={handleAddEmployee}
                        color="primary"
                    >
                        Create Employee
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );

    // Render the edit employee form
    const renderEditForm = () => (
        <Card sx={{ width: '100%', mt: 3, mb: 3 }} elevation={3}>
            <CardContent>
                <FormFields isEdit={true} />
                <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                    <Button
                        variant="outlined"
                        startIcon={<Cancel />}
                        onClick={() => toggleScreen('read')}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        startIcon={<EditIcon />}
                        onClick={handleUpdateEmployee}
                    >
                        Update Employee
                    </Button>
                </Stack>
            </CardContent>
        </Card>
    );

    return (
        <ThemeProvider theme={theme}>
            <Container
                maxWidth="lg"
                sx={{
                    maxHeight: '80vh', // Set a maximum height instead of fixed height
                    overflowY: 'auto', // Enable vertical scrolling
                    width: '100%',
                    py: 2
                }}
            >
                <Box sx={{ mb: 3 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h4" color="primary">
                            {screen === 'read' && 'Employees'}
                            {screen === 'write' && 'Create Employee'}
                            {screen === 'modify' && 'Edit Employee'}
                        </Typography>

                        {screen === 'read' ? (
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => toggleScreen('write')}
                            >
                                Add Employee
                            </Button>
                        ) : (
                            <Button
                                variant="contained"
                                color="warning"
                                startIcon={<Close />}
                                onClick={() => toggleScreen('read')}
                            >
                                Cancel
                            </Button>
                        )}
                    </Box>
                    <Divider sx={{ mt: 2 }} />
                </Box>

                {/* Main Content */}
                <Box>
                    {screen === 'read' && renderEmployeeTable()}
                    {screen === 'write' && renderCreateForm()}
                    {screen === 'modify' && renderEditForm()}
                </Box>

                {/* Alert Snackbar */}
                <Snackbar
                    open={alert.open}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseAlert}
                        severity={alert.severity}
                        variant="filled"
                        sx={{ width: '100%' }}
                    >
                        {alert.message}
                    </Alert>
                </Snackbar>
            </Container>
        </ThemeProvider>
    );
};

export default EmployeeManagement;